import { useState, useContext } from "react";
import { Check, Loader2 } from "lucide-react";
import { NewsletterContext } from "../context/NewsletterContext";
import { useNLEmails } from "../hooks/useNLEmails";
import { useAuth } from "@/auth/useAuth";
import { useGenerateNLwTone } from "../hooks/useGenerateNLwTone";

export const SetupComplete: React.FC = () => {
  const { session } = useAuth();
  const email = session?.user.user_metadata.email ?? "";
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showSuccess, setShowSuccess] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const {
    config: { topics, tone, frequency, preferredDay, toneFeedback },
    preview,
    updatePreview,
  } = useContext(NewsletterContext);

  const { sendEmail } = useNLEmails();
  const { generateNewsletter, isGenerating } = useGenerateNLwTone();

  const handleSendTest = async () => {
    if (!email) return;

    let newsletterContent = preview;

    // Generate newsletter first if it doesn't exist
    if (!newsletterContent) {
      try {
        const generated = await generateNewsletter(
          tone || "",
          toneFeedback || ""
        );
        updatePreview(generated);
        newsletterContent = generated;
      } catch (error) {
        console.error("Error generating newsletter:", error);
        setError("Failed to generate newsletter");
        return;
      }
    }

    // Verify we have content before sending
    if (!newsletterContent?.subject || !newsletterContent?.body) {
      setError("Newsletter content is missing");
      return;
    }

    try {
      setIsSubmitting(true);
      setError(null);
      const { error } = await sendEmail(
        newsletterContent.subject,
        newsletterContent.body,
        [email]
      );

      if (error) {
        setError(error.message);
        return;
      }

      setShowSuccess(true);
    } catch (error) {
      console.error("Error sending test email:", error);
      setError("Failed to send test email");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleGeneratePreview = async () => {
    try {
      const generated = await generateNewsletter(
        tone || "",
        toneFeedback || ""
      );
      updatePreview(generated);
    } catch (error) {
      console.error("Error generating preview:", error);
      setError("Failed to generate preview");
    }
  };

  return (
    <div
      className="border border-black rounded-xl overflow-hidden shadow-md mb-12"
      style={{
        background:
          "linear-gradient(135deg, #dcfce7 0%, #d1fae5 50%, #a7f3d0 100%)",
      }}
    >
      <div
        className="p-6 border-b border-black"
        style={{
          background: "linear-gradient(135deg, #86efac 0%, #6ee7b7 100%)",
        }}
      >
        <h2 className="text-xl font-medium mb-2">Setup Complete! 🎉</h2>
        <p className="text-gray-700">
          Your personal AI newsletter is all set up. Send yourself a test email
          to see it in action.
        </p>
      </div>

      <div className="p-6">
        <div className="bg-white border border-black rounded-lg p-6 mb-6">
          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">
              <Check className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">Topics selected</p>
              <p className="text-sm text-gray-600">{topics.join(", ")}</p>
            </div>
          </div>

          <div className="flex items-center mb-4">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">
              <Check className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">Tone configured</p>
              <p className="text-sm text-gray-600">{tone}</p>
            </div>
          </div>

          <div className="flex items-center">
            <div className="w-8 h-8 bg-green-500 text-white rounded-full flex items-center justify-center mr-3">
              <Check className="h-5 w-5" />
            </div>
            <div>
              <p className="font-medium">Delivery schedule set</p>
              <p className="text-sm text-gray-600">
                {frequency} {preferredDay ? `on ${preferredDay}` : ""}
              </p>
            </div>
          </div>
        </div>

        <div className="space-y-4">
          {!preview && (
            <button
              onClick={handleGeneratePreview}
              disabled={isGenerating}
              className="w-full py-4 bg-black text-white text-base font-medium rounded-lg transition-all hover:bg-gray-800 hover:rotate-1 hover:transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
            >
              {isGenerating ? (
                <>
                  <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                  Generating Preview...
                </>
              ) : (
                "Generate Newsletter Preview"
              )}
            </button>
          )}

          {preview && (
            <>
              <label className="text-base font-medium block">
                Send a test newsletter to your email
              </label>
              <input
                type="email"
                value={email || ""}
                placeholder="youremail@example.com"
                className="w-full p-4 border border-black text-base bg-white rounded-lg focus:outline-none focus:ring-2 focus:ring-green-500"
                readOnly
              />
              {error ? (
                <p className="mx-2 text-sm text-red-500">{error}</p>
              ) : (
                <p className="mx-2 text-sm text-gray-600">
                  We'll send an example of your personalized newsletter to this
                  address
                </p>
              )}

              <button
                onClick={handleSendTest}
                disabled={!email || isSubmitting}
                className="w-full py-4 bg-black text-white text-base font-medium rounded-lg transition-all hover:bg-gray-800 hover:rotate-1 hover:transform disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center"
              >
                {isSubmitting ? (
                  <>
                    <Loader2 className="animate-spin -ml-1 mr-3 h-5 w-5" />
                    Sending...
                  </>
                ) : (
                  "Send Test Newsletter"
                )}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Success Modal */}
      {showSuccess && (
        <div className="fixed inset-0 flex items-center justify-center z-50 bg-black bg-opacity-50">
          <div
            className="bg-white border border-black p-8 max-w-md w-full rounded-xl shadow-lg"
            style={{
              background: "linear-gradient(135deg, #dcfce7 0%, #d1fae5 100%)",
            }}
          >
            <div className="text-center mb-4">
              <div className="w-16 h-16 bg-green-500 rounded-full flex items-center justify-center mx-auto mb-4">
                <Check className="h-8 w-8 text-white" />
              </div>
              <h2 className="text-xl font-medium mb-2">Success!</h2>
              <p className="text-gray-800 mb-6">
                Test newsletter sent to <strong>{email}</strong>
              </p>
            </div>

            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Please check your inbox (and spam folder) shortly.
              </p>

              <button
                onClick={() => setShowSuccess(false)}
                className="w-full py-3 bg-black text-white font-medium rounded-lg transition-all hover:bg-gray-800 hover:-rotate-1 hover:transform"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};
